const STORAGE_KEY = 'gs-cc-widget-dimensions'

/**
 * Height categories inferred from widget type keywords.
 * Used as fallback when no cached measurement exists.
 */
const HEIGHT_HEURISTICS: Array<{ pattern: RegExp; height: number }> = [
  { pattern: /header|nav|breadcrumb|menu/i, height: 56 },
  { pattern: /banner|hero|jumbotron|cover/i, height: 250 },
  { pattern: /footer/i, height: 80 },
  { pattern: /sidebar|rail/i, height: 300 },
  { pattern: /leaderboard|ranking/i, height: 320 },
  { pattern: /topic|post|article|discussion|feed/i, height: 400 },
  { pattern: /form|search|filter/i, height: 120 }
]

const DEFAULT_HEIGHT = 120

let memCache: Record<string, number> | null = null

function readCache(): Record<string, number> {
  if (memCache) return memCache
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    memCache = raw ? JSON.parse(raw) : {}
  } catch {
    memCache = {}
  }
  return memCache as Record<string, number>
}

function writeCache(cache: Record<string, number>): void {
  memCache = cache
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache))
  } catch {
    // localStorage unavailable or quota exceeded — silently skip
  }
}

/**
 * Returns the best placeholder height estimate (in px) for a widget type.
 * Checks localStorage cache first, then falls back to keyword heuristics.
 */
export function getEstimatedHeight(widgetType: string): number {
  const cache = readCache()
  if (cache[widgetType] != null) {
    return cache[widgetType]
  }

  return inferHeightFromType(widgetType)
}

/** Keyword-based height inference for uncached widget types. */
function inferHeightFromType(widgetType: string): number {
  for (const { pattern, height } of HEIGHT_HEURISTICS) {
    if (pattern.test(widgetType)) return height
  }
  return DEFAULT_HEIGHT
}

/** @internal Flush the in-memory cache — only needed for test isolation. */
export function _resetMemoryCache(): void {
  memCache = null
}

/**
 * Records the actual rendered height of a widget type for future placeholder sizing.
 * Only updates when the new height differs meaningfully (>10px) from the cached value,
 * avoiding excessive localStorage writes on minor reflow variations.
 */
export function cacheRenderedHeight(widgetType: string, height: number): void {
  if (height <= 0) return

  const rounded = Math.round(height)
  const cache = readCache()
  const existing = cache[widgetType]

  if (existing != null && Math.abs(existing - rounded) <= 10) return

  cache[widgetType] = rounded
  writeCache(cache)
}
